import React, { useMemo, useState } from "react";
import { useSecurityContext } from "@/contexts/MaintenanceContext";
import { ImportarCatalogoDialog } from "@/components/inventario/ImportarCatalogoDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Pencil,
  Trash2,
  Plus,
  Shield,
  Flame,
  Search,
  Eye,
  EyeOff,
  Factory,
  Settings,
  FileSpreadsheet,
  X,
  Lock as LockIcon,
  LayoutGrid,
  List as ListIcon,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ALL_SYSTEM_TYPES, getSystemCategory } from "@/lib/systemTypeConstants";
import { getSystemTypeIcon, getCategoryIcon as getCategoryLucideIcon } from "@/lib/systemTypeIcons";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

type EditState = { id: string | null; value: string };
type SystemType = { tipo: string; nombre: string; categoria: 'seguridad' | 'pci' };

const getSystemIcon = getSystemTypeIcon;
const getCategoryIcon = getCategoryLucideIcon;

const getCategoryColor = (categoria: 'seguridad' | 'pci') =>
  categoria === 'seguridad'
    ? 'bg-blue-50 border-blue-200 text-blue-900 dark:bg-blue-950/30 dark:text-blue-100'
    : 'bg-orange-50 border-orange-200 text-orange-900 dark:bg-orange-950/30 dark:text-orange-100';

const normalizar = (txt: string) =>
  (txt || '').toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();

export default function CatalogoEquipos() {
  const ctx = useSecurityContext() as any;
  const {
    marcasEquipo, modelosEquipo, tiposEquipo,
    editarMarcaEquipo, eliminarMarcaEquipo,
    editarModeloEquipo, eliminarModeloEquipo,
    editarTipoEquipo, eliminarTipoEquipo,
    crearMarcaEquipo, crearModeloEquipo, crearTipoEquipo,
    sincronizarTiposEquipo, sincronizarMarcasEquipo, sincronizarModelosEquipo,
    recursosOcultos, ocultarRecurso, mostrarRecurso,
  } = ctx;

  const perfil = ctx.perfil;
  // empresa_id no está expuesto directamente; los items propios se identifican por empresa_id !== null
  // y RLS garantiza que si vemos un item con empresa_id, es el nuestro.
  const { toast } = useToast();

  const [activeSystemType, setActiveSystemType] = useState("CCTV");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<'todos' | 'seguridad' | 'pci'>('todos');
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [mostrarOcultos, setMostrarOcultos] = useState(false);
  const [viewMode, setViewMode] = useState<'cards' | 'lista'>('cards');

  // Filtros activos por sistema
  const [activeTipo, setActiveTipo] = useState<string | null>(null);
  const [activeMarca, setActiveMarca] = useState<string | null>(null);

  // Edit states
  const [editMarca, setEditMarca] = useState<EditState>({ id: null, value: "" });
  const [editModelo, setEditModelo] = useState<EditState>({ id: null, value: "" });
  const [editTipo, setEditTipo] = useState<EditState>({ id: null, value: "" });

  // Create new
  const [nuevaMarca, setNuevaMarca] = useState("");
  const [nuevoModelo, setNuevoModelo] = useState("");
  const [nuevoTipo, setNuevoTipo] = useState("");
  const [marcaParaNuevoModelo, setMarcaParaNuevoModelo] = useState("");

  const systemTypes: SystemType[] = Object.entries(ALL_SYSTEM_TYPES).map(([tipo, nombre]) => ({
    tipo, nombre, categoria: getSystemCategory(tipo),
  }));
  const filteredSystemTypes = selectedCategory === 'todos'
    ? systemTypes
    : systemTypes.filter(s => s.categoria === selectedCategory);

  const sistemaActivoNorm = normalizar(activeSystemType);
  const matchesSearch = (s?: string) =>
    !searchQuery || normalizar(s || '').includes(normalizar(searchQuery));

  const isOculto = (recurso: 'tipo' | 'marca' | 'modelo', id: string) => {
    if (recurso === 'tipo') return recursosOcultos?.tipos?.has(id);
    if (recurso === 'marca') return recursosOcultos?.marcas?.has(id);
    return recursosOcultos?.modelos?.has(id);
  };

  // Datos del sistema
  const tiposDelSistema = useMemo(() =>
    (tiposEquipo as any[])
      .filter(t => normalizar(t.sistema_tipo || '') === sistemaActivoNorm)
      .filter(t => mostrarOcultos || !isOculto('tipo', t.id))
      .filter(t => matchesSearch(t.nombre))
      .sort((a, b) => a.nombre.localeCompare(b.nombre)),
    [tiposEquipo, sistemaActivoNorm, mostrarOcultos, recursosOcultos, searchQuery]);

  const marcasDelSistema = useMemo(() =>
    (marcasEquipo as any[])
      .filter(m => normalizar(m.sistema_tipo || '') === sistemaActivoNorm)
      .filter(m => mostrarOcultos || !isOculto('marca', m.id))
      .filter(m => matchesSearch(m.nombre))
      .sort((a, b) => a.nombre.localeCompare(b.nombre)),
    [marcasEquipo, sistemaActivoNorm, mostrarOcultos, recursosOcultos, searchQuery]);

  const modelosDelSistema = useMemo(() => {
    const tipoActivoObj = (tiposEquipo as any[]).find(t => t.id === activeTipo);
    const tipoActivoNombreNorm = tipoActivoObj ? normalizar(tipoActivoObj.nombre) : null;
    return (modelosEquipo as any[])
      .filter(m => normalizar(m.sistema_tipo || '') === sistemaActivoNorm)
      .filter(m => mostrarOcultos || !isOculto('modelo', m.id))
      .filter(m => !activeMarca || m.marca_id === activeMarca)
      .filter(m => !tipoActivoNombreNorm || (m.tipo_equipo && normalizar(m.tipo_equipo) === tipoActivoNombreNorm))
      .filter(m => matchesSearch(m.nombre))
      .sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [modelosEquipo, tiposEquipo, sistemaActivoNorm, activeMarca, activeTipo, mostrarOcultos, recursosOcultos, searchQuery]);

  const getMarcaById = (id: string) => (marcasEquipo as any[]).find(m => m.id === id);

  const stats = useMemo(() => {
    const selectedSystem = systemTypes.find(s => s.tipo === activeSystemType);
    return {
      tipos: tiposDelSistema.length,
      marcas: marcasDelSistema.length,
      modelos: modelosDelSistema.length,
      categoria: selectedSystem?.categoria || 'seguridad' as 'seguridad' | 'pci',
    };
  }, [tiposDelSistema.length, marcasDelSistema.length, modelosDelSistema.length, activeSystemType]);

  // Handlers
  const handleCrearTipo = async () => {
    if (!nuevoTipo.trim()) return;
    await crearTipoEquipo(nuevoTipo.trim(), activeSystemType);
    setNuevoTipo("");
  };
  const handleCrearMarca = async () => {
    if (!nuevaMarca.trim()) return;
    await crearMarcaEquipo(nuevaMarca.trim(), activeSystemType);
    setNuevaMarca("");
  };
  const handleCrearModelo = async () => {
    const marcaId = marcaParaNuevoModelo || activeMarca;
    if (!nuevoModelo.trim() || !marcaId) {
      toast({ title: "Falta marca", description: "Selecciona una marca para el nuevo modelo.", variant: "destructive" });
      return;
    }
    const tipoObj = (tiposEquipo as any[]).find(t => t.id === activeTipo);
    await crearModeloEquipo(nuevoModelo.trim(), marcaId, activeSystemType, tipoObj?.nombre);
    setNuevoModelo("");
    setMarcaParaNuevoModelo("");
  };

  const renderPill = (
    item: any,
    recurso: 'tipo' | 'marca',
    isActive: boolean,
    onToggle: () => void,
  ) => {
    const propio = !!item.empresa_id;
    const oculto = isOculto(recurso, item.id);
    const editState = recurso === 'tipo' ? editTipo : editMarca;
    const setEditState = recurso === 'tipo' ? setEditTipo : setEditMarca;
    const editarFn = recurso === 'tipo' ? editarTipoEquipo : editarMarcaEquipo;
    const eliminarFn = recurso === 'tipo' ? eliminarTipoEquipo : eliminarMarcaEquipo;

    if (editState.id === item.id) {
      return (
        <div key={item.id} className="flex items-center gap-1 bg-background border border-primary rounded-full pl-3 pr-1 py-1">
          <Input
            value={editState.value}
            onChange={(e) => setEditState({ ...editState, value: e.target.value })}
            className="h-7 w-32 text-xs border-0 focus-visible:ring-0"
            autoFocus
            onKeyDown={async (e) => {
              if (e.key === 'Enter') {
                await editarFn(item.id, { nombre: editState.value });
                setEditState({ id: null, value: "" });
              } else if (e.key === 'Escape') {
                setEditState({ id: null, value: "" });
              }
            }}
          />
          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={async () => {
            await editarFn(item.id, { nombre: editState.value });
            setEditState({ id: null, value: "" });
          }}>✓</Button>
          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setEditState({ id: null, value: "" })}>
            <X className="h-3 w-3" />
          </Button>
        </div>
      );
    }

    return (
      <div
        key={item.id}
        className={cn(
          "group inline-flex items-center gap-1 rounded-full border pl-3 pr-1 py-1 text-sm transition-colors cursor-pointer",
          isActive
            ? "bg-primary text-primary-foreground border-primary"
            : "bg-muted/40 hover:bg-muted border-border",
          oculto && "opacity-50"
        )}
        onClick={onToggle}
      >
        <span className="font-medium select-none">{item.nombre}</span>
        {!propio && (
          <span title="Recurso global" className="opacity-60">
            <LockIcon className="h-3 w-3" />
          </span>
        )}
        <div className="ml-1 flex items-center opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
          {propio && (
            <>
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setEditState({ id: item.id, value: item.nombre })}>
                <Pencil className="h-3 w-3" />
              </Button>
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-destructive" onClick={() => {
                if (confirm(`¿Eliminar "${item.nombre}"?`)) eliminarFn(item.id);
              }}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="h-6 w-6 p-0"
            title={oculto ? "Mostrar" : "Ocultar"}
            onClick={() => oculto ? mostrarRecurso(recurso, item.id) : ocultarRecurso(recurso, item.id)}
          >
            {oculto ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
          </Button>
        </div>
      </div>
    );
  };

  const CategoryIcon = getCategoryIcon(stats.categoria);
  const SystemIcon = getSystemIcon(activeSystemType);
  const activeTipoNombre = (tiposEquipo as any[]).find(t => t.id === activeTipo)?.nombre;
  const activeMarcaNombre = getMarcaById(activeMarca || '')?.nombre;

  return (
    <div className="container mx-auto p-4 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Factory className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Catálogo de Equipos por Sistema</h1>
            <p className="text-muted-foreground text-sm">Gestiona tipos, marcas y modelos por sistema</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border bg-background p-0.5">
            <Button
              variant={viewMode === 'cards' ? 'default' : 'ghost'}
              size="sm"
              className="h-7 gap-1 px-2"
              onClick={() => setViewMode('cards')}
              title="Vista tarjetas"
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'lista' ? 'default' : 'ghost'}
              size="sm"
              className="h-7 gap-1 px-2"
              onClick={() => setViewMode('lista')}
              title="Vista lista"
            >
              <ListIcon className="h-4 w-4" />
            </Button>
          </div>
          <Button
            variant={mostrarOcultos ? 'default' : 'outline'}
            size="sm"
            onClick={() => setMostrarOcultos(v => !v)}
            className="gap-2"
          >
            {mostrarOcultos ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            {mostrarOcultos ? 'Ver todos' : 'Mostrar ocultos'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setImportDialogOpen(true)} className="gap-2">
            <FileSpreadsheet className="h-4 w-4" />
            Importar
          </Button>
        </div>
      </div>

      {/* Search + Category */}
      <div className="flex flex-col sm:flex-row gap-4 py-4 border-b">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Buscar en el catálogo..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
        </div>
        <div className="flex gap-2">
          <Button variant={selectedCategory === 'todos' ? 'default' : 'outline'} onClick={() => setSelectedCategory('todos')} size="sm">Todos</Button>
          <Button variant={selectedCategory === 'seguridad' ? 'default' : 'outline'} onClick={() => setSelectedCategory('seguridad')} size="sm" className="gap-1">
            <Shield className="h-4 w-4" /> Seguridad
          </Button>
          <Button variant={selectedCategory === 'pci' ? 'default' : 'outline'} onClick={() => setSelectedCategory('pci')} size="sm" className="gap-1">
            <Flame className="h-4 w-4" /> PCI
          </Button>
        </div>
      </div>

      <Tabs value={activeSystemType} onValueChange={(v) => { setActiveSystemType(v); setActiveTipo(null); setActiveMarca(null); }}>
        <TabsList className="mb-6 flex-wrap h-auto p-1">
          {filteredSystemTypes.map((st) => {
            const Icon = getSystemIcon(st.tipo);
            return (
              <TabsTrigger key={st.tipo} value={st.tipo} className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                <Icon className="h-4 w-4" />
                {st.nombre}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {filteredSystemTypes.map((st) => (
          <TabsContent key={st.tipo} value={st.tipo} className="mt-0 space-y-4">
            {/* Stats header */}
            <div className={`rounded-lg border p-4 ${getCategoryColor(st.categoria)}`}>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-white/50">
                    <SystemIcon className="h-6 w-6" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold">{st.nombre}</h2>
                    <div className="flex items-center gap-1 text-sm opacity-80">
                      <CategoryIcon className="h-4 w-4" />
                      {st.categoria === 'seguridad' ? 'Sistema de Seguridad' : 'Protección Contra Incendios'}
                    </div>
                  </div>
                </div>
                <div className="flex gap-6 text-right">
                  <div><div className="text-2xl font-bold">{stats.tipos}</div><div className="text-xs opacity-80">Tipos</div></div>
                  <div><div className="text-2xl font-bold">{stats.marcas}</div><div className="text-xs opacity-80">Marcas</div></div>
                  <div><div className="text-2xl font-bold">{stats.modelos}</div><div className="text-xs opacity-80">Modelos</div></div>
                </div>
              </div>
            </div>

            {/* TIPOS */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Settings className="h-4 w-4 text-primary" />
                  Tipos de equipo
                  <Badge variant="secondary" className="ml-2">{tiposDelSistema.length}</Badge>
                  {activeTipo && (
                    <Button variant="ghost" size="sm" className="ml-auto h-7 text-xs" onClick={() => setActiveTipo(null)}>
                      <X className="h-3 w-3 mr-1" /> Quitar filtro
                    </Button>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  {tiposDelSistema.map(tipo =>
                    renderPill(tipo, 'tipo', activeTipo === tipo.id, () =>
                      setActiveTipo(activeTipo === tipo.id ? null : tipo.id)
                    )
                  )}
                  {tiposDelSistema.length === 0 && (
                    <div className="text-sm text-muted-foreground py-2">No hay tipos de equipo.</div>
                  )}
                </div>
                <div className="flex gap-2 pt-2 border-t">
                  <Input
                    placeholder="Nuevo tipo..."
                    value={nuevoTipo}
                    onChange={(e) => setNuevoTipo(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleCrearTipo()}
                    className="max-w-xs h-9"
                  />
                  <Button size="sm" onClick={handleCrearTipo} className="gap-1">
                    <Plus className="h-4 w-4" /> Añadir
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* MARCAS */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Factory className="h-4 w-4 text-primary" />
                  Marcas
                  <Badge variant="secondary" className="ml-2">{marcasDelSistema.length}</Badge>
                  {activeMarca && (
                    <Button variant="ghost" size="sm" className="ml-auto h-7 text-xs" onClick={() => setActiveMarca(null)}>
                      <X className="h-3 w-3 mr-1" /> Quitar filtro
                    </Button>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  {marcasDelSistema.map(marca =>
                    renderPill(marca, 'marca', activeMarca === marca.id, () =>
                      setActiveMarca(activeMarca === marca.id ? null : marca.id)
                    )
                  )}
                  {marcasDelSistema.length === 0 && (
                    <div className="text-sm text-muted-foreground py-2">No hay marcas.</div>
                  )}
                </div>
                <div className="flex gap-2 pt-2 border-t">
                  <Input
                    placeholder="Nueva marca..."
                    value={nuevaMarca}
                    onChange={(e) => setNuevaMarca(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleCrearMarca()}
                    className="max-w-xs h-9"
                  />
                  <Button size="sm" onClick={handleCrearMarca} className="gap-1">
                    <Plus className="h-4 w-4" /> Añadir
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* MODELOS */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                  <Eye className="h-4 w-4 text-primary" />
                  Modelos
                  <Badge variant="secondary" className="ml-1">{modelosDelSistema.length}</Badge>
                  {(activeTipo || activeMarca) && (
                    <div className="flex items-center gap-2 ml-2 text-xs font-normal text-muted-foreground">
                      Filtrando por:
                      {activeTipoNombre && <Badge variant="outline">Tipo: {activeTipoNombre}</Badge>}
                      {activeMarcaNombre && <Badge variant="outline">Marca: {activeMarcaNombre}</Badge>}
                    </div>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {viewMode === 'lista' ? (
                  <div className="border rounded-md max-h-[520px] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50 sticky top-0 z-10">
                        <tr className="text-left text-xs text-muted-foreground">
                          <th className="px-3 py-2 font-medium">Modelo</th>
                          <th className="px-3 py-2 font-medium">Marca</th>
                          <th className="px-3 py-2 font-medium">Tipo</th>
                          <th className="px-3 py-2 font-medium w-px">Origen</th>
                          <th className="px-3 py-2 font-medium w-px text-right">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {modelosDelSistema.map((modelo: any) => {
                          const marca = getMarcaById(modelo.marca_id);
                          const propio = !!modelo.empresa_id;
                          const oculto = isOculto('modelo', modelo.id);
                          const editing = editModelo.id === modelo.id;
                          return (
                            <tr
                              key={modelo.id}
                              className={cn(
                                "border-t hover:bg-muted/40 transition-colors",
                                oculto && "opacity-50"
                              )}
                            >
                              <td className="px-3 py-2 font-medium">
                                {editing ? (
                                  <div className="flex items-center gap-1">
                                    <Input
                                      value={editModelo.value}
                                      onChange={(e) => setEditModelo({ ...editModelo, value: e.target.value })}
                                      className="h-7 text-sm"
                                      autoFocus
                                    />
                                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={async () => {
                                      await editarModeloEquipo(modelo.id, { nombre: editModelo.value });
                                      setEditModelo({ id: null, value: "" });
                                    }}>✓</Button>
                                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditModelo({ id: null, value: "" })}>
                                      <X className="h-3 w-3" />
                                    </Button>
                                  </div>
                                ) : (
                                  modelo.nombre
                                )}
                              </td>
                              <td className="px-3 py-2 text-muted-foreground">{marca?.nombre || '—'}</td>
                              <td className="px-3 py-2 text-muted-foreground">{modelo.tipo_equipo || '—'}</td>
                              <td className="px-3 py-2">
                                {propio ? (
                                  <Badge variant="outline" className="text-[10px]">Propio</Badge>
                                ) : (
                                  <Badge variant="secondary" className="text-[10px] gap-1"><LockIcon className="h-3 w-3" />Global</Badge>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                <div className="flex justify-end">
                                  {propio && !editing && (
                                    <>
                                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditModelo({ id: modelo.id, value: modelo.nombre })}>
                                        <Pencil className="h-3 w-3" />
                                      </Button>
                                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive" onClick={() => {
                                        if (confirm(`¿Eliminar "${modelo.nombre}"?`)) eliminarModeloEquipo(modelo.id);
                                      }}>
                                        <Trash2 className="h-3 w-3" />
                                      </Button>
                                    </>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 w-7 p-0"
                                    title={oculto ? "Mostrar" : "Ocultar"}
                                    onClick={() => oculto ? mostrarRecurso('modelo', modelo.id) : ocultarRecurso('modelo', modelo.id)}
                                  >
                                    {oculto ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                        {modelosDelSistema.length === 0 && (
                          <tr>
                            <td colSpan={5} className="text-center py-8 text-muted-foreground text-sm">
                              No hay modelos con los filtros actuales.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 max-h-[420px] overflow-y-auto">
                    {modelosDelSistema.map((modelo: any) => {
                      const marca = getMarcaById(modelo.marca_id);
                      const propio = !!modelo.empresa_id;
                      const oculto = isOculto('modelo', modelo.id);
                      const editing = editModelo.id === modelo.id;
                      return (
                        <div
                          key={modelo.id}
                          className={cn(
                            "group p-3 rounded-md border bg-muted/30 hover:bg-muted/50 transition-colors",
                            oculto && "opacity-50"
                          )}
                        >
                          <div className="flex items-center gap-1 mb-1 flex-wrap">
                            {marca && <Badge variant="outline" className="text-[10px]">{marca.nombre}</Badge>}
                            {modelo.tipo_equipo && <Badge variant="outline" className="text-[10px]">{modelo.tipo_equipo}</Badge>}
                            {!propio && <span title="Global" className="opacity-60"><LockIcon className="h-3 w-3" /></span>}
                          </div>
                          {editing ? (
                            <div className="flex items-center gap-1">
                              <Input
                                value={editModelo.value}
                                onChange={(e) => setEditModelo({ ...editModelo, value: e.target.value })}
                                className="h-7 text-sm"
                                autoFocus
                              />
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={async () => {
                                await editarModeloEquipo(modelo.id, { nombre: editModelo.value });
                                setEditModelo({ id: null, value: "" });
                              }}>✓</Button>
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditModelo({ id: null, value: "" })}>
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-medium text-sm truncate">{modelo.nombre}</span>
                              <div className="flex opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                                {propio && (
                                  <>
                                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditModelo({ id: modelo.id, value: modelo.nombre })}>
                                      <Pencil className="h-3 w-3" />
                                    </Button>
                                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive" onClick={() => {
                                      if (confirm(`¿Eliminar "${modelo.nombre}"?`)) eliminarModeloEquipo(modelo.id);
                                    }}>
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </>
                                )}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0"
                                  title={oculto ? "Mostrar" : "Ocultar"}
                                  onClick={() => oculto ? mostrarRecurso('modelo', modelo.id) : ocultarRecurso('modelo', modelo.id)}
                                >
                                  {oculto ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {modelosDelSistema.length === 0 && (
                      <div className="col-span-full text-center py-8 text-muted-foreground text-sm">
                        No hay modelos con los filtros actuales.
                      </div>
                    )}
                  </div>
                )}

                <div className="flex flex-wrap gap-2 pt-3 border-t items-center">
                  <select
                    value={marcaParaNuevoModelo || activeMarca || ''}
                    onChange={(e) => setMarcaParaNuevoModelo(e.target.value)}
                    className="border rounded-md px-2 py-1.5 text-sm bg-background h-9"
                  >
                    <option value="">Marca...</option>
                    {marcasDelSistema.map((m: any) => (
                      <option key={m.id} value={m.id}>{m.nombre}</option>
                    ))}
                  </select>
                  <Input
                    placeholder="Nuevo modelo..."
                    value={nuevoModelo}
                    onChange={(e) => setNuevoModelo(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleCrearModelo()}
                    className="max-w-xs h-9"
                  />
                  <Button size="sm" onClick={handleCrearModelo} className="gap-1">
                    <Plus className="h-4 w-4" /> Añadir
                  </Button>
                  {activeTipo && (
                    <span className="text-xs text-muted-foreground">
                      Se asociará al tipo "{activeTipoNombre}"
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      <ImportarCatalogoDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        onSuccess={() => {
          sincronizarTiposEquipo();
          sincronizarMarcasEquipo();
          sincronizarModelosEquipo();
        }}
      />
    </div>
  );
}
